import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import games, { ACTIVE_GAME_SOURCE_NAME } from "../data/games";
import GamePlayer from "../components/GamePlayer";
import { auth, db } from "../firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getFavoritesForUser, toggleFavoriteForUser } from "../utils/favorites";
import Seo from "../components/Seo";
import { incrementGameClick } from "../utils/gamePopularity";
import GameWalkthrough from "../components/GameWalkthrough";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://octogames.io").replace(
  /\/+$/,
  ""
);

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const formatDimension = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "—";
  return String(Math.round(parsed));
};

const mergeCounter = (raw, key) => {
  if (!key) return raw && typeof raw === "object" ? { ...raw } : {};
  const base = raw && typeof raw === "object" ? { ...raw } : {};
  base[key] = (Number(base[key]) || 0) + 1;
  return base;
};

const GamePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const game = games.find((g) => g.slug === slug);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const playerWrapperRef = useRef(null);

  const info = useMemo(() => {
    if (!game) {
      return {
        languages: [],
        genres: [],
        tags: [],
        width: "—",
        height: "—",
        embedUrl: "",
        sourceName: ACTIVE_GAME_SOURCE_NAME,
        published: "—",
        updated: "—"
      };
    }
    return {
      languages: Array.isArray(game.languages) ? game.languages : [],
      genres: Array.isArray(game.genres) ? game.genres : [],
      tags: Array.isArray(game.tags)
        ? game.tags
        : typeof game.tags === "string"
          ? game.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
      width: formatDimension(game.width),
      height: formatDimension(game.height),
      embedUrl: game.embedUrl || game.url || "",
      sourceName: game.sourceName || ACTIVE_GAME_SOURCE_NAME,
      published: formatDate(game.firstActiveDate),
      updated: formatDate(game.lastUpdated)
    };
  }, [game]);

  useEffect(() => {
    const recordPlay = async () => {
      const user = auth.currentUser;
      if (!user || !game) return;

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : { recentGames: [], totalPlays: 0 };
        const now = new Date();
        const existing = Array.isArray(data.recentGames)
          ? data.recentGames.filter((g) => g && g.id && g.id !== game.id)
          : [];
        const updated = [
          {
            id: game.id,
            slug: game.slug,
            title: game.title,
            lastPlayedAt: { seconds: Math.floor(now.getTime() / 1000) }
          },
          ...existing
        ].slice(0, 10);

        const categoryPlayCounts = mergeCounter(data.categoryPlayCounts, game.category || "");
        let genrePlayCounts = data.genrePlayCounts && typeof data.genrePlayCounts === "object"
          ? { ...data.genrePlayCounts }
          : {};
        if (Array.isArray(game.genres)) {
          game.genres.forEach((genre) => {
            genrePlayCounts = mergeCounter(genrePlayCounts, genre);
          });
        }

        await setDoc(
          ref,
          {
            recentGames: updated,
            totalPlays: (data.totalPlays || 0) + 1,
            categoryPlayCounts,
            genrePlayCounts,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      } catch {
        // ignore stats errors in UI
      }
    };

    recordPlay();
  }, [game]);

  useEffect(() => {
    if (!game) return undefined;
    const startedAtMs = Date.now();

    return () => {
      const user = auth.currentUser;
      if (!user) return;

      const elapsedSeconds = Math.max(5, Math.floor((Date.now() - startedAtMs) / 1000));

      (async () => {
        try {
          const ref = doc(db, "users", user.uid);
          const snap = await getDoc(ref);
          const data = snap.exists() ? snap.data() : {};
          const byGameRaw = data.playtimeByGameId;
          const playtimeByGameId = byGameRaw && typeof byGameRaw === "object"
            ? { ...byGameRaw }
            : {};
          playtimeByGameId[game.id] = (Number(playtimeByGameId[game.id]) || 0) + elapsedSeconds;

          await setDoc(
            ref,
            {
              totalPlaytimeSeconds: (Number(data.totalPlaytimeSeconds) || 0) + elapsedSeconds,
              playtimeByGameId,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
        } catch {
          // ignore playtime persistence errors
        }
      })();
    };
  }, [game]);

  useEffect(() => {
    const recordGlobalClick = async () => {
      if (!game) return;
      try {
        await incrementGameClick(game);
      } catch {
        // ignore popularity counter failures in UI
      }
    };

    recordGlobalClick();
  }, [game]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);
      if (!user || !game) {
        setIsFavorite(false);
        return;
      }
      try {
        const favorites = await getFavoritesForUser(user.uid);
        setIsFavorite(favorites.some((f) => f.id === game.id));
      } catch {
        setIsFavorite(false);
      }
    });
    return () => unsub();
  }, [game]);

  const handleToggleFavorite = async () => {
    if (!game) return;
    if (!currentUser) {
      navigate("/account?mode=signin");
      return;
    }
    try {
      const updated = await toggleFavoriteForUser(currentUser.uid, game);
      setIsFavorite(updated.some((f) => f.id === game.id));
    } catch {
      // ignore toggle failures
    }
  };

  if (!game) {
    return (
      <main className="page page-game">
        <Seo
          title="Game Not Found"
          description="The requested game could not be found in the OctoGames catalog."
          path={`/game/${slug || ""}`}
          noindex
        />
        <div className="container">
          <div className="game-not-found">
            <h1>Game not found</h1>
            <p>The game you are looking for does not exist in the catalog.</p>
            <Link to="/" className="btn primary">
              Back to all games
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const normalizedDescription = (game.description || "").replace(/\\n/g, "\n");
  const normalizedInstructions = (game.instructions || "").replace(/\\n/g, "\n");
  const hasResolution = info.width !== "—" && info.height !== "—";
  const isPortraitGame =
    hasResolution && Number(info.width) > 0 && Number(info.height) > 0
      ? Number(info.width) < Number(info.height)
      : false;
  const hasWalkthrough = game.sourceKey === "gm" && Boolean(game.id);
  const gameJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: game.title,
      genre: Array.isArray(game.genres) ? game.genres : undefined,
      description:
        (game.description || "Play this game online on OctoGames.").slice(0, 220),
      image: game.thumbnail || undefined,
      publisher: game.publisher
        ? { "@type": "Organization", name: game.publisher }
        : undefined,
      url: `${SITE_URL}/game/${game.slug}`
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`
        },
        {
          "@type": "ListItem",
          position: 2,
          name: game.title,
          item: `${SITE_URL}/game/${game.slug}`
        }
      ]
    }
  ];

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  return (
    <main className="page page-game">
      <Seo
        title={`${game.title} - Play Online`}
        description={
          (game.description || `Play ${game.title} online for free on OctoGames.`).slice(
            0,
            160
          )
        }
        path={`/game/${game.slug}`}
        image={game.thumbnail || "/logo192.png"}
        type="article"
        keywords={`${game.title}, play ${game.title} online, ${game.category || "online game"}, browser game`}
        jsonLd={gameJsonLd}
      />
      <div className="container game-layout">
        <header className="game-header">
          <div className="game-header-main">
            <div className="game-header-title">
              <h1 className="game-title">{game.title}</h1>
              {game.category && (
                <span className="game-category-pill">{game.category}</span>
              )}
            </div>
            {game.publisher && (
              <p className="game-publisher">Published by {game.publisher}</p>
            )}
          </div>
          <div className="game-header-actions">
            <button
              type="button"
              className={"game-favorite-btn" + (isFavorite ? " game-favorite-btn-active" : "")}
              onClick={handleToggleFavorite}
            >
              {isFavorite ? "♥ In favorites" : "♡ Add to favorites"}
            </button>
            <Link to="/" className="game-back-btn">
              ← Back to games
            </Link>
          </div>
        </header>

        <section className="game-main">
          <div className="game-primary">
            <div
              ref={playerWrapperRef}
              className={
                "game-player-fullscreen-wrapper" +
                (isFullscreen ? " game-player-fullscreen-active" : "")
              }
            >
              <GamePlayer gameId={game.id} gameSlug={game.slug} embedUrl={info.embedUrl} />
              {isPortraitGame && (
                <button
                  type="button"
                  className="game-fullscreen-toggle"
                  onClick={handleToggleFullscreen}
                >
                  {isFullscreen ? "Close full screen" : "Open in full screen"}
                </button>
              )}
            </div>
          </div>

          <aside className="game-meta">
            <div className="game-meta-section">
              <h2 className="game-meta-title">Overview</h2>
              <div className="game-meta-grid">
                {game.category && (
                  <div className="game-meta-item">
                    <span className="game-meta-label">Category</span>
                    <span className="game-meta-value">{game.category}</span>
                  </div>
                )}
                {hasResolution && (
                  <div className="game-meta-item">
                    <span className="game-meta-label">Resolution</span>
                    <span className="game-meta-value">{`${info.width} × ${info.height}`}</span>
                  </div>
                )}
                {info.languages.length > 0 && (
                  <div className="game-meta-item">
                    <span className="game-meta-label">Languages</span>
                    <div className="game-meta-chips">
                      {info.languages.map((lang) => (
                        <span key={lang} className="chip">
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {info.published !== "—" && (
                  <div className="game-meta-item">
                    <span className="game-meta-label">Published</span>
                    <span className="game-meta-value">{info.published}</span>
                  </div>
                )}
                {info.updated !== "—" && (
                  <div className="game-meta-item">
                    <span className="game-meta-label">Updated</span>
                    <span className="game-meta-value">{info.updated}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="game-meta-section">
              <h2 className="game-meta-title">Genres</h2>
              <div className="game-meta-chips">
                {info.genres.length ? (
                  info.genres.map((g) => (
                    <span key={g} className="chip chip-soft">
                      {g}
                    </span>
                  ))
                ) : (
                  <span className="game-meta-value">—</span>
                )}
              </div>
            </div>

            <div className="game-meta-section">
              <h2 className="game-meta-title">Tags</h2>
              <div className="game-meta-chips">
                {info.tags.length ? (
                  info.tags.map((t) => (
                    <span key={t} className="chip chip-outline">
                      {t}
                    </span>
                  ))
                ) : (
                  <span className="game-meta-value">—</span>
                )}
              </div>
            </div>

            {(game.sourceUrl || info.embedUrl) && (
              <div className="game-meta-section">
                <a
                  href={game.sourceUrl || info.embedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="game-source-link"
                >
                  Open source page ({info.sourceName}) ↗
                </a>
              </div>
            )}
          </aside>
        </section>

        <section className="game-secondary">
          <div className="game-details-block">
            <h2 className="game-meta-title">Description</h2>
            <p className="game-description">
              {normalizedDescription || "No description available yet."}
            </p>
          </div>

          <div className="game-details-block">
            <h2 className="game-meta-title">How to play</h2>
            <pre className="game-instructions-pre">
              {normalizedInstructions || "Instructions are not available for this game yet."}
            </pre>
          </div>
        </section>

        {hasWalkthrough && (
          <section className="game-walkthrough-section">
            <div className="game-details-block game-walkthrough-block">
              <h2 className="game-meta-title">Walkthrough</h2>
              <GameWalkthrough gameId={game.id} showAds={false} />
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default GamePage;

